import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../../components/BaseUI/Modal";
import Button from "../../../../components/BaseUI/Button";
import Input from "../../../../components/BaseUI/Input";
import Label from "../../../../components/BaseUI/Label";
import Select from "../../../../components/BaseUI/Select";
import {
  formatDateTimeTrInAppTz,
  dateStrInAppTz,
} from "../../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const PLANNED_ID = "__planned__";
const todayStr = () => dateStrInAppTz(new Date());

const formatStopDuration = (log) => {
  if (log.durationMinutes != null) return `${log.durationMinutes} dk`;
  if (log.endTime && log.startTime) {
    const mins = Math.round(
      (new Date(log.endTime) - new Date(log.startTime)) / 60000,
    );
    return `${mins} dk`;
  }
  return "Devam ediyor";
};

const defaultPlannedStart = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const StopReasonModal = ({
  isOpen,
  onClose,
  machine,
  initialStopLogId,
  initialStopDate,
  getToken,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [stopLogId, setStopLogId] = useState("");
  const [stopReason, setStopReason] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(initialStopDate || todayStr());
  const [plannedStart, setPlannedStart] = useState(defaultPlannedStart);
  const [plannedEnd, setPlannedEnd] = useState("");
  const [plannedDuration, setPlannedDuration] = useState("");

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  const resetForm = useCallback(() => {
    setFormData(null);
    setError("");
    setStopLogId(initialStopLogId ? String(initialStopLogId) : "");
    setStopReason("");
    setNotes("");
    setDate(initialStopDate || todayStr());
    setPlannedStart(defaultPlannedStart());
    setPlannedEnd("");
    setPlannedDuration("");
  }, [initialStopLogId, initialStopDate]);

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen, resetForm]);

  const fetchFormData = useCallback(
    async (dateVal) => {
      if (!machine?._id) return;
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          machineId: machine._id,
          action: "stop",
          date: dateVal || todayStr(),
        });
        const { data } = await axios.get(
          `${API_URL}/api/epc/form-data?${params}`,
          { headers: headers() },
        );
        setFormData(data);
      } catch (err) {
        setError(
          err?.response?.data?.message || "Duruş kayıtları yüklenemedi",
        );
      } finally {
        setLoading(false);
      }
    },
    [machine?._id, headers],
  );

  useEffect(() => {
    if (isOpen && machine?._id) {
      fetchFormData(date);
    }
  }, [isOpen, machine?._id, date, fetchFormData]);

  const pendingStops = formData?.pendingStops || [];
  const isPlanned = stopLogId === PLANNED_ID;

  useEffect(() => {
    if (!isOpen || !initialStopLogId || !pendingStops.length) return;
    const id = String(initialStopLogId);
    const match = pendingStops.find((s) => String(s._id) === id);
    if (match) {
      setStopLogId(id);
      const reasonId = match.stopReason?._id || match.stopReason;
      if (reasonId) setStopReason(String(reasonId));
    }
  }, [isOpen, initialStopLogId, pendingStops]);

  const stopOpts = [
    {
      value: PLANNED_ID,
      label: "Planlı duruş ekle",
    },
    ...pendingStops.map((s) => ({
      value: s._id,
      label: `${formatDateTimeTrInAppTz(s.startTime)} — ${formatStopDuration(s)}${s.stopReason ? ` (${s.stopReason.name})` : " (Nedensiz)"}`,
    })),
  ];

  const srOpts = (formData?.stopReasons || []).map((r) => ({
    value: r._id,
    label: r.name,
  }));

  const selectedStop = !isPlanned
    ? pendingStops.find((s) => String(s._id) === String(stopLogId))
    : null;

  const durationNum =
    plannedDuration !== "" && plannedDuration != null
      ? Number(plannedDuration)
      : null;
  const durationOk =
    plannedDuration === "" ||
    plannedDuration == null ||
    (Number.isFinite(durationNum) && durationNum > 0);

  const canSave =
    !!stopReason &&
    (isPlanned
      ? !!plannedStart &&
        (!plannedEnd || new Date(plannedEnd) > new Date(plannedStart)) &&
        durationOk
      : !!stopLogId && stopLogId !== PLANNED_ID);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError("");
    try {
      if (isPlanned) {
        await axios.post(
          `${API_URL}/api/epc/planned-stop`,
          {
            machineId: machine._id,
            startTime: new Date(plannedStart).toISOString(),
            endTime: plannedEnd
              ? new Date(plannedEnd).toISOString()
              : undefined,
            durationMinutes:
              durationNum != null &&
              Number.isFinite(durationNum) &&
              durationNum > 0
                ? durationNum
                : undefined,
            stopReason,
            notes,
          },
          { headers: headers() },
        );
      } else {
        await axios.post(
          `${API_URL}/api/epc/stop`,
          { stopLogId, stopReason, notes },
          { headers: headers() },
        );
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Duruş kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDateChange = (nextDate) => {
    setDate(nextDate);
    if (!initialStopLogId) setStopLogId("");
  };

  const machineLabel = machine?.name || machine?.code || "";

  return (
    <Modal
      isOpen={isOpen}
      width="medium"
      scrollBehavior="inside"
      onClose={saving ? undefined : onClose}
      className="mlm-entry-modal"
    >
      <ModalHeader>
        Duruş Kaydı{machineLabel ? ` — ${machineLabel}` : ""}
      </ModalHeader>
      <ModalBody showCloseButton={false}>
        {loading ? (
          <div className="mlm-entry-modal__loading">Form yükleniyor...</div>
        ) : (
          <div className="mlm-entry-form">
            <div className="mlm-entry-form__field">
              <Label>Tarih</Label>
              <Input
                type="date"
                value={date}
                onChange={handleDateChange}
                disabled={saving}
              />
            </div>

            <div className="mlm-entry-form__field">
              <Select
                label="Duruş Kaydı"
                required
                unique
                disabled={saving}
                placeholder={
                  loading ? "Duruşlar yükleniyor..." : "Duruş seçiniz"
                }
                options={stopOpts}
                selected={stopLogId ? [stopLogId] : []}
                onSelected={(items) =>
                  setStopLogId(items?.[0]?.value ? String(items[0].value) : "")
                }
              />
            </div>

            {isPlanned && (
              <>
                <div className="mlm-entry-form__field">
                  <Label required>Başlangıç</Label>
                  <Input
                    type="datetime-local"
                    value={plannedStart}
                    onChange={setPlannedStart}
                    disabled={saving}
                  />
                </div>
                <div className="mlm-entry-form__row">
                  <div className="mlm-entry-form__field">
                    <Label>Bitiş</Label>
                    <Input
                      type="datetime-local"
                      value={plannedEnd}
                      onChange={setPlannedEnd}
                      disabled={saving}
                    />
                  </div>
                  <div className="mlm-entry-form__field">
                    <Label>Süre (dakika)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={plannedDuration}
                      onChange={setPlannedDuration}
                      placeholder="Bitiş girilmezse"
                      disabled={saving}
                    />
                  </div>
                </div>
              </>
            )}

            {selectedStop && (
              <div className="mlm-entry-form__preview">
                <div>
                  <span>Başlangıç</span>
                  <strong>
                    {formatDateTimeTrInAppTz(selectedStop.startTime)}
                  </strong>
                </div>
                <div>
                  <span>Süre</span>
                  <strong>{formatStopDuration(selectedStop)}</strong>
                </div>
                {selectedStop.endTime && (
                  <div>
                    <span>Bitiş</span>
                    <strong>
                      {formatDateTimeTrInAppTz(selectedStop.endTime)}
                    </strong>
                  </div>
                )}
              </div>
            )}

            {!!stopLogId && (
              <>
                <div className="mlm-entry-form__field">
                  <Select
                    label="Duruş Nedeni"
                    required
                    unique
                    disabled={saving}
                    placeholder="Neden seçiniz"
                    options={srOpts}
                    selected={stopReason ? [stopReason] : []}
                    onSelected={(items) =>
                      setStopReason(
                        items?.[0]?.value ? String(items[0].value) : "",
                      )
                    }
                  />
                </div>
                <div className="mlm-entry-form__field">
                  <Label>Not</Label>
                  <Input
                    value={notes}
                    onChange={setNotes}
                    placeholder="İsteğe bağlı not..."
                    disabled={saving}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {error ? <div className="mlm-entry-form__error">{error}</div> : null}
      </ModalBody>
      <ModalFooter>
        <Button type="button" disabled={saving} onClick={onClose}>
          İptal
        </Button>
        <Button
          appearance="primary-animated"
          type="button"
          disabled={saving || loading || !canSave}
          onClick={handleSave}
        >
          {saving
            ? "Kaydediliyor..."
            : isPlanned
              ? "Planlı Duruş Kaydet"
              : "Nedeni Ata"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default StopReasonModal;
